import { Icons, Text, defaultTheme } from '@agro-club/agroclub-shared'
import { refetchFunc } from 'modules/domain/common/hooks'
import { CurrencySign, Export, HedgeType } from 'modules/domain/export/types'
import formatNumber from 'modules/utils/numbers/formatNumber'
import formatPrice from 'modules/utils/numbers/formatPrice'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { AButton } from 'views/components/Analytics'
import { Badge, BgBadgeColors } from 'views/components/Badge/Badge'
import { Card } from 'views/components/Card/Card'
import PermissionCheck from 'views/components/PermissionCheck/PermissionCheck'
import { Price } from '../../components/Price'
import { Rates } from '../Rates'
import { AddHedgingModal } from './HedgingModals/AddHedgingModal'
import { EditHedgingModal } from './HedgingModals/EditHedgingModal'

const StyledCardContainer = styled(Card.Container).attrs({ intent: 'backgroundPrimary' })`
  width: 432px;
  color: ${defaultTheme.color.textPrimary900};
`

const NameCost = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Hr = styled.div`
  height: 1px;
  background: ${defaultTheme.color.secondary100};
`

const HedgeTotalTitleWrapper = styled.div`
  display: flex;
  column-gap: 4px;
`

const colorMap: Record<string, BgBadgeColors> = {
  [HedgeType.option]: 'primary',
  [HedgeType.forward]: 'purple',
  [HedgeType.natural]: 'red',
}

type Props = {
  exportData: Export
  exportRefetch: refetchFunc
}
export const CostInfo: FC<Props> = ({ exportData, exportRefetch }) => {
  const { t } = useTranslation('export')
  const hasHedge = !!exportData.hedge_id

  const [addHedgingModal, setAddHedgingModal] = useState(false)
  const [editHedgingModal, setEditHedgingModal] = useState(false)
  const hedgeTotalBadge = (
    <Badge color={colorMap[exportData.hedge_type]}>{t(`hedgeType.${exportData.hedge_type}`)}</Badge>
  )

  return (
    <StyledCardContainer>
      <Card.GapableContent noPaddingForEmpty={false}>
        <NameCost>
          <Text typography="bodyMedium">{t('contractValue')}</Text>
          <Text typography="titleH4">
            {`${formatNumber(exportData.price, true)} ${t('formatPrice', {
              currency: CurrencySign[exportData.price_currency],
            })}`}
          </Text>
        </NameCost>
        <Rates exportData={exportData} />
        <Price
          localizedPrice={exportData.through_dubai_localized_price}
          currency={exportData.through_dubai_price_currency}
          price={exportData.through_dubai_price}
          label={t('priceThroughDubai')}
        />
        <Price
          localizedPrice={exportData.freight_cost_localized_price}
          currency={exportData.freight_currency}
          price={exportData.freight_cost}
          label={t('freight')}
        />
        <Price
          localizedPrice={exportData.warehouse_cost_localized_price}
          currency={exportData.warehouse_currency}
          price={exportData.warehouse_cost}
          label={t('transshipment')}
        />
        <Price
          localizedPrice={exportData.storage_cost_localized_price}
          currency={exportData.storage_currency}
          price={exportData.storage_cost}
          label={t('storage')}
        />
        <Price
          localizedPrice={exportData.export_broker_cost_localized_price}
          currency={exportData.export_broker_currency}
          price={exportData.export_broker_cost}
          label={t('broker')}
        />
        <Price
          localizedPrice={exportData.freight_broker_cost_localized_price}
          currency={exportData.freight_broker_currency}
          price={exportData.freight_broker_cost}
          label={t('freightBroker')}
        />
        <Price
          localizedPrice={exportData.documents_cost_localized_price}
          currency={exportData.documents_currency}
          price={exportData.documents_cost}
          label={t('documents')}
        />
        <Price
          localizedPrice={exportData.duty_cost_localized_price}
          currency={exportData.duty_currency}
          price={exportData.duty_cost}
          label={t('toll')}
        />
        <Price
          localizedPrice={exportData.insurance_cost_localized_price}
          currency={exportData.insurance_currency}
          price={exportData.insurance_cost}
          label={t('insurance')}
        />
        <Price
          localizedPrice={exportData.surveyor_cost_localized_price}
          currency={exportData.surveyor_currency}
          price={exportData.surveyor_cost}
          label={t('surveyor')}
        />
        <Price
          localizedPrice={exportData.forwarder_cost_localized_price}
          currency={exportData.forwarder_currency}
          price={exportData.forwarder_cost}
          label={t('forwarder')}
        />
        <Hr />
        <NameCost>
          <Text typography="bodyMedium">{t('total')}</Text>
          <Text typography="titleH4">{formatPrice(exportData.raw_total, true)}</Text>
        </NameCost>
        {!!exportData.hedged_total && (
          <NameCost>
            <HedgeTotalTitleWrapper>
              <Text typography="bodyMedium">{t('hedging')}</Text>
              {hedgeTotalBadge}
            </HedgeTotalTitleWrapper>
            <Text typography="accentMedium">{formatPrice(exportData.hedged_total, true)}</Text>
          </NameCost>
        )}
        {!!exportData.final_total && (
          <NameCost>
            <Text typography="bodyMedium">{t('oneC')}</Text>
            <Text typography="accentMedium">{formatPrice(exportData.final_total, true)}</Text>
          </NameCost>
        )}
        <PermissionCheck scope={['export.view_exportcontracthedge']}>
          {hasHedge ? (
            <AButton
              id="editHedging"
              size="big"
              color="secondary"
              background="none"
              border="solid"
              onClick={() => setEditHedgingModal(true)}
            >
              {t('editHedging')}
            </AButton>
          ) : (
            <AButton
              id="addHedging"
              size="big"
              Icon={Icons.IconAdd}
              color="secondary"
              background="none"
              border="dashed"
              onClick={() => setAddHedgingModal(true)}
            >
              {t('hedging')}
            </AButton>
          )}
        </PermissionCheck>
        {addHedgingModal && (
          <AddHedgingModal
            onClose={() => setAddHedgingModal(false)}
            exportId={exportData.id}
            onSuccess={exportRefetch}
          />
        )}
        {editHedgingModal && (
          <EditHedgingModal
            onSuccess={exportRefetch}
            onClose={() => setEditHedgingModal(false)}
            exportId={exportData.id}
            exportHedgeId={exportData.hedge_id}
          />
        )}
      </Card.GapableContent>
    </StyledCardContainer>
  )
}
